import React from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faEdit,
	faEye,
	faRedo,
	faMobileAlt,
} from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import {
	DEP_STATE_ACCEPTED,
	DEP_STATE_CANCELED,
	DEP_STATE_CHECK_DATA,
	DEP_STATE_DISPATCHED,
	DEP_STATE_EN_ATTENTE,
	DEP_STATE_EN_SUSPENS,
	DEP_STATE_ONGOING,
	DEP_STATE_READY_TO_DISPATCH,
	DEP_STATE_RENDEZ_VOUS,
	DEP_STATES,
	DEP_TYPE_TRANSPORT,
	DEPANNAGE_TYPE_APPEL_PRIVE,
} from "../../../utilities/Addons"
import { getLieuAffichage } from "../../../utilities/depLieux"
import { SECTEURS_POLICE, VEHICULE_CATEGORIE } from "../../../utilities/Utils"
import {
	DEPANNAGE_TYPE_APPEL_ASSISTANCE,
	DEPANNAGE_TYPE_APPEL_POLICE,
	TYPE_ACCIDENT,
} from "../depmission/DepMissionV2"
import { formatNumDossier } from "../depFicheUtils"
import LigneDossierCarte from "../assistances/LigneDossierCarte"
import { texteInterchangeable } from "../assistances/assistancesUi"
import {
	useCouvertureMission,
	useResumeMission,
} from "../assistances/AssistancesContext"

// Couleurs de badge par état de mission (fallbacks littéraux : la palette
// COLORS n'a pas de déclinaison par état).
const ETAT_BADGE = {
	[DEP_STATE_CHECK_DATA]: { color: "#B45309", bg: "#FEF3C7" },
	[DEP_STATE_READY_TO_DISPATCH]: { color: "#166534", bg: "#DCFCE7" },
	[DEP_STATE_EN_SUSPENS]: { color: "#7C2D12", bg: "#FFEDD5" },
	[DEP_STATE_EN_ATTENTE]: { color: "#1D4ED8", bg: "#DBEAFE" },
	[DEP_STATE_RENDEZ_VOUS]: { color: "#6D28D9", bg: "#EDE9FE" },
	[DEP_STATE_DISPATCHED]: { color: "#166534", bg: "#DCFCE7" },
	[DEP_STATE_ONGOING]: { color: "#0E7490", bg: "#CFFAFE" },
	[DEP_STATE_ACCEPTED]: { color: "#166534", bg: "#DCFCE7" },
	[DEP_STATE_CANCELED]: { color: COLORS.danger, bg: COLORS.dangerLight },
}

// Mission liée à une fiche dépannage : badge « Relivraison » + bouton
// d'ouverture de la fiche. Partagés avec les autres vues du dispatch (En
// cours, Annulées, modal Traiter). B4 (CDC 16.10) : UN seul mot pour la
// mission qui ramène le véhicule du dépôt à sa destination finale — créée
// depuis la fiche (`est_livraison`) ou par la conversion (suite d'une
// dépose, transport lié) ; l'infobulle dit laquelle.
export const estRelivraison = (dep) => !!dep.dep

export const RelivraisonBadge = ({ dep }) =>
	estRelivraison(dep) ? (
		<span
			style={{ ...st.badge, ...st.badgeRelivraison }}
			title={
				dep.est_livraison
					? "Relivraison créée depuis la fiche"
					: "Relivraison créée à la conversion (véhicule déposé au dépôt)"
			}
		>
			<FontAwesomeIcon icon={faRedo} style={{ fontSize: 10 }} />
			Relivraison
		</span>
	) : null

// GE-47 : mission créée sur le terrain par le dépanneur de piquet — badge
// « Terrain · créée par X » (nom résolu depuis la liste des employés).
export const TerrainBadge = ({ dep }) => {
	const { employes } = useSelector((state) => state.masterReducer)
	if (!dep.cree_terrain) return null
	const createur = (employes || []).find((e) => e.id === dep.cree_par)
	return (
		<span
			style={{ ...st.badge, ...st.badgeTerrain }}
			title="Mission créée sur le terrain (dispatch de piquet)"
		>
			<FontAwesomeIcon icon={faMobileAlt} style={{ fontSize: 10 }} />
			{createur ? `Terrain · créée par ${createur.nom}` : "Terrain"}
		</span>
	)
}

// Même onglet (décision Jordan 01.10) via le router : le modal éventuel
// (Traiter, détail En cours) est rendu en ligne et se démonte avec le
// dispatch. <Link> garde le ctrl/clic-milieu natifs.
export const OuvrirFicheBtn = ({ ficheId, petit = false }) =>
	ficheId ? (
		<Link
			to={`/depannage/fiche/${ficheId}`}
			style={
				petit
					? { ...st.iconePetite, textDecoration: "none" }
					: {
							...gs.iconBtn(COLORS.text),
							boxSizing: "border-box",
							textDecoration: "none",
						}
			}
			onClick={(e) => e.stopPropagation()}
			title="Ouvrir la fiche"
			aria-label="Ouvrir la fiche"
		>
			<FontAwesomeIcon icon={faEye} />
		</Link>
	) : null

// Carte mission COMPACTE du dispatch (GE-57 P5 bis, maquette validée
// docs/correlation-assistances/maquette-assistances.html) — commune à
// À traiter, En cours et Annulées. De haut en bas : titre « Type · M{id} »
// (+ icônes et heure), pastilles (état précisé, appel, suite…), trajet,
// véhicule, client (police : secteur · clé), diagnostic / montant, ligne
// dossier 📁, action. L'état est porté par la pastille (plus de bordure
// gauche colorée). Téléphone et n° de dossier : dans la fiche / le modal.
// - `onClick` : toute la carte est cliquable (En cours → détail) ;
// - `statut` : précision temporelle (En cours : « Arrivé sur place à 08:07 ») ;
// - `action` : { label, onClick, secondaire } remplace Vérifier / Traiter.
const TARIF_AU_TERRAIN = "Tarif au terrain"
// Mentions neutres (gris) : rien à compléter par le bureau.
const MENTIONS_NEUTRES = [
	TARIF_AU_TERRAIN,
	"Forfait relivraison",
	"Rien à facturer",
]

// GE-66 K8 (Jordan 13.10) : privé → tarif calculé (relivraison au forfait
// comprise), sinon le présumé s'il existe (anciennes missions, RDV, livraison),
// sinon « Tarif au terrain », neutre : le bureau n'a rien à compléter.
// Passe 4 : privé annulé → « Rien à facturer » (🟡6) ; relivraison pas encore
// calculée → « Forfait relivraison » (🟡5) ; un tarif à 0 reste un tarif (🟡7).
// GE-75 : « Annoncé : 230.– / 480.– » (sur place / transporté), « Annoncé :
// 480.– » (transport d'office), « Sous contrat » ; null sans annonce.
export const texteAnnonce = (annonce) => {
	if (!annonce || !annonce.tr) return null
	if (annonce.sous_contrat) return "Sous contrat"
	const prix = (p) => (p.devis ? "devis" : montantCarte(p.total))
	return annonce.sp
		? `Annoncé : ${prix(annonce.sp)} / ${prix(annonce.tr)}`
		: `Annoncé : ${prix(annonce.tr)}`
}

const montantPrive = (dep) => {
	const prive = dep.appel_type === DEPANNAGE_TYPE_APPEL_PRIVE
	if (prive && dep.etat === DEP_STATE_CANCELED) return "Rien à facturer"
	if (prive && dep.prive_total != null)
		return `Tarif : ${montantCarte(dep.prive_total)}`
	if (prive && dep.prive_relivraison) return "Forfait relivraison"
	// GE-75 : prix ANNONCÉ au client à la création (calculateur complet).
	const annonce = prive ? texteAnnonce(dep.prive_annonce) : null
	if (annonce) return annonce
	if (dep.total_presume > 0)
		return `Présumé : ${montantCarte(dep.total_presume)}`
	return prive && !dep.est_livraison ? TARIF_AU_TERRAIN : null
}

const DepMissionCardV2 = ({
	dep,
	onVerifier,
	onTraiter,
	onEdit,
	onClick = null,
	statut = null,
	action: actionForcee = null,
	// Onglet Assistances (09.10) : carte dépliée en LECTURE seule — ni
	// action, ni œil / crayon, ni ligne 📁, ni clic (liens Maps conservés).
	lectureSeule = false,
}) => {
	const { employes } = useSelector((state) => state.masterReducer)
	const resume = useResumeMission(dep.id)
	const couverture = useCouvertureMission(dep.id)
	const badge = ETAT_BADGE[dep.etat] || {
		color: COLORS.textMuted,
		bg: COLORS.bg,
	}
	const isPolice = dep.appel_type === DEPANNAGE_TYPE_APPEL_POLICE
	const isAssistance = dep.appel_type === DEPANNAGE_TYPE_APPEL_ASSISTANCE
	const depanneur = (employes || []).find((e) => e.id === dep.depanneur)

	const lieu = getLieuAffichage(dep.lieu_intervention, [
		dep.lieu_intervention_0,
		dep.lieu_intervention_1,
		dep.lieu_intervention_2,
	])
	const destination = getLieuAffichage(dep.destination_finale, [
		dep.destination_finale_0,
		dep.destination_finale_1,
		dep.destination_finale_2,
	])
	// Trajet : départ (puce pleine) → destination (anneau). Police : lieu seul.
	const etapes = []
	if (lieu) etapes.push({ key: "lieu", lieu, plein: true })
	if (!isPolice && destination)
		etapes.push({ key: "dest", lieu: destination, plein: false })

	const action = lectureSeule
		? null
		: actionForcee ||
			(dep.etat === DEP_STATE_CHECK_DATA
				? { label: "Vérifier", onClick: () => onVerifier(dep) }
				: [DEP_STATE_READY_TO_DISPATCH, DEP_STATE_EN_ATTENTE].includes(
							dep.etat,
					  )
					? { label: "Traiter", onClick: () => onTraiter(dep) }
					: null)

	// Montant : celui de l'ordre d'assistance s'il existe (« Accordé »), sinon
	// le présumé ; rouge si absent. Police : tarif calculé à la clôture.
	// GE-66 J4 / K8 : voir montantPrive.
	const montant =
		resume && resume.montant !== null && resume.montant !== undefined
			? `Accordé : ${montantCarte(resume.montant, resume.devise)}`
			: montantPrive(dep)

	const racineProps =
		onClick && !lectureSeule
			? {
					role: "button",
					tabIndex: 0,
					onClick: () => onClick(dep),
					// Entrée sur un enfant (ligne 📁, liens) : pas le détail.
					onKeyDown: (e) => {
						if (e.key === "Enter" && e.target === e.currentTarget)
							onClick(dep)
					},
				}
			: { role: "listitem" }

	return (
		<div
			className="dm-card-v2"
			style={{
				...st.card,
				cursor: onClick && !lectureSeule ? "pointer" : "default",
			}}
			{...racineProps}
		>
			<div style={st.header}>
				<span style={st.titre}>
					{dep.type === DEP_TYPE_TRANSPORT
						? "Transport"
						: "Intervention"}{" "}
					· M{dep.id}
				</span>
				<div style={st.headerActions}>
					{!lectureSeule && (
						<OuvrirFicheBtn ficheId={dep.dep} petit />
					)}
					{onEdit && !lectureSeule && (
						<button
							type="button"
							style={st.iconePetite}
							onClick={(e) => {
								e.stopPropagation()
								onEdit(dep)
							}}
							title="Modifier la mission"
							aria-label="Modifier la mission"
						>
							<FontAwesomeIcon icon={faEdit} />
						</button>
					)}
					<span style={st.heure}>{heureCarte(dep.cree_le)}</span>
				</div>
			</div>

			<div style={st.badges}>
				<span
					style={{
						...st.badge,
						color: badge.color,
						backgroundColor: badge.bg,
					}}
				>
					● {libelleEtat(dep, depanneur)}
				</span>
				{isPolice && (
					<span style={{ ...st.badge, ...st.badgeAppel }}>
						Police
					</span>
				)}
				{dep.appel_type === DEPANNAGE_TYPE_APPEL_PRIVE && (
					<span style={{ ...st.badge, ...st.badgeAppel }}>Privé</span>
				)}
				{/* Assistance : son nom est dans la ligne 📁 ; sans dossier lié,
				    une pastille le garde visible. */}
				{isAssistance && !resume && (
					<span style={{ ...st.badge, ...st.badgeAppel }}>
						{dep.assistance_entite_label ||
							dep.appel ||
							"Assistance"}
					</span>
				)}
				{isPolice && dep.accident_ou_enlevement > 0 ? (
					<span
						style={{
							...st.badge,
							color: COLORS.accent,
							backgroundColor: COLORS.accentLight,
						}}
					>
						{dep.accident_ou_enlevement === TYPE_ACCIDENT
							? "Accident"
							: "Enlèvement"}
					</span>
				) : null}
				<RelivraisonBadge dep={dep} />
				<TerrainBadge dep={dep} />
				<CouvertBadge
					couverture={couverture}
					dep={dep}
					onOuvrirMission={onEdit || onClick}
					lectureSeule={lectureSeule}
				/>
			</div>
			{statut ? <div style={st.statut}>{statut}</div> : null}

			{etapes.length > 0 && (
				<div className="dm-route" style={st.route}>
					{etapes.length > 1 && <div style={st.routeConnecteur} />}
					{etapes.map((e) => {
						const texte = e.lieu.lines
							.filter((l) => l && l !== "Suisse")
							.join(", ")
						return (
							<div key={`${e.key}_${dep.id}`} style={st.routeRow}>
								<span style={st.routePuce(e.plein)} />
								{e.lieu.mapsUrl ? (
									<a
										href={e.lieu.mapsUrl}
										target="_blank"
										rel="noopener noreferrer"
										style={st.routeTexte}
										onClick={(ev) => ev.stopPropagation()}
										title="Ouvrir dans Maps"
									>
										{texte}
									</a>
								) : (
									<span style={st.routeTexte}>{texte}</span>
								)}
							</div>
						)
					})}
				</div>
			)}

			{(dep.plaques || dep.marque || dep.modele || !isPolice) && (
				<Emoji ico="🚗">
					{dep.plaques ? (
						<span style={st.plaque}>{dep.plaques}</span>
					) : null}
					{[dep.marque, dep.modele].filter(Boolean).join(" ") ||
						(dep.plaques ? "" : "—")}
				</Emoji>
			)}
			{/* Police sans plaque ni véhicule : la catégorie, sinon pas de ligne. */}
			{isPolice &&
				!dep.plaques &&
				!dep.marque &&
				!dep.modele &&
				VEHICULE_CATEGORIE[dep.vehicule_categorie] && (
					<Emoji ico="🚗">
						{VEHICULE_CATEGORIE[dep.vehicule_categorie]}
					</Emoji>
				)}
			{isPolice ? (
				// Ligne masquée sans secteur ni clé (même règle que le 🚗).
				secteurCle(dep) ? (
					<Emoji ico="📍">{secteurCle(dep)}</Emoji>
				) : null
			) : (
				<Emoji ico="👤">
					{`${dep.nom} ${dep.prenom}`.trim() || "—"}
				</Emoji>
			)}
			{dep.diagnostique_presume ? (
				<Emoji ico="🔧">{dep.diagnostique_presume}</Emoji>
			) : null}
			{resume &&
				(resume.interchangeables || []).map((x) => (
					<Emoji key={`inter_${x.mission}`} ico="⇄">
						<span style={{ color: COLORS.textMuted }}>
							{texteInterchangeable(x)}
						</span>
					</Emoji>
				))}
			{/* GE-59 (A5, Jordan) : sans ligne 📁 (assistance off ou sans
			    dossier), le n° de dossier reste lisible sur la carte. */}
			{!resume && (dep.num_dossier || "").trim() ? (
				<Emoji ico="#">{formatNumDossier(dep.num_dossier)}</Emoji>
			) : null}
			{!isPolice && (
				<Emoji ico="💶" alerte={!montant}>
					{MENTIONS_NEUTRES.includes(montant) ? (
						<span style={{ color: COLORS.textMuted }}>
							{montant}
						</span>
					) : (
						montant || "Montant à définir"
					)}
				</Emoji>
			)}

			{/* Bloc collé en bas (Jordan 08.10) : ligne 📁 juste au-dessus du
			    bouton, même hauteur, au même endroit sur toute la rangée. */}
			<div style={st.pied}>
				{!lectureSeule && <LigneDossierCarte missionId={dep.id} />}
				{action && (
					<button
						type="button"
						style={
							action.secondaire ? st.boutonSecondaire : st.bouton
						}
						onClick={(e) => {
							e.stopPropagation()
							action.onClick()
						}}
					>
						{action.label}
					</button>
				)}
			</div>
		</div>
	)
}

// GE-60 : pastille « ✓ Couvert 540.– » (« Couvert » sans plafond) quand une
// couverture active est rattachée à la mission ou à sa fiche. Clic → la
// fiche sur sa carte Couvertures, ou la fenêtre de la mission si elle n'est
// pas convertie.
const CouvertBadge = ({
	couverture,
	dep,
	onOuvrirMission,
	lectureSeule = false,
}) => {
	if (!couverture) return null
	const texte =
		couverture.montant !== null && couverture.montant !== undefined
			? `✓ Couvert ${montantCarte(couverture.montant)}`
			: "✓ Couvert"
	const titre = `Couvert par ${couverture.assistance}`
	const style = { ...st.badge, ...st.badgeCouvert }
	const ficheId = couverture.fiche_id || dep.dep
	if (lectureSeule)
		return (
			<span style={style} title={titre}>
				{texte}
			</span>
		)
	if (ficheId)
		return (
			<Link
				to={`/depannage/fiche/${ficheId}#couvertures`}
				style={{ ...style, textDecoration: "none" }}
				onClick={(e) => e.stopPropagation()}
				title={`${titre} — voir la fiche`}
			>
				{texte}
			</Link>
		)
	if (!onOuvrirMission)
		return (
			<span style={style} title={titre}>
				{texte}
			</span>
		)
	return (
		<button
			type="button"
			style={{ ...style, cursor: "pointer", fontFamily: "inherit" }}
			onClick={(e) => {
				e.stopPropagation()
				onOuvrirMission(dep)
			}}
			title={`${titre} — ouvrir la mission`}
		>
			{texte}
		</button>
	)
}

// Police : « secteur · clé », "" si les deux sont vides.
const secteurCle = (dep) =>
	[
		SECTEURS_POLICE[dep.secteur_police],
		dep.avec_cle === 1
			? "Avec clé"
			: dep.avec_cle === 2
				? "Sans clé"
				: null,
	]
		.filter(Boolean)
		.join(" · ")

// Ligne icône (emoji, comme la maquette) + contenu.
const Emoji = ({ ico, alerte = false, children }) => (
	<div style={st.ligne}>
		<span style={st.ligneIcone} role="img" aria-hidden="true">
			{ico}
		</span>
		<div
			style={{
				...st.ligneTexte,
				...(alerte ? { color: COLORS.danger, fontWeight: 600 } : null),
			}}
		>
			{children}
		</div>
	</div>
)

// « 30-09-2026 18:16 » (serializer) → « 30.09 18:16 ».
export const heureCarte = (creeLe) => {
	const m = /^(\d{2})-(\d{2})-\d{4} (\d{2}:\d{2})/.exec(creeLe || "")
	return m ? `${m[1]}.${m[2]} ${m[3]}` : creeLe || ""
}

const montantCarte = (valeur, devise) => {
	const n = Number(valeur)
	const txt = Number.isInteger(n) ? `${n}.–` : `${n.toFixed(2)}`
	return devise && devise !== "CHF" ? `${txt} ${devise}` : txt
}

// Initiales du dépanneur (« Fortunato Alex » → « F. A. »).
const initiales = (nom) =>
	(nom || "")
		.split(/\s+/)
		.filter(Boolean)
		.map((p) => `${p[0].toUpperCase()}.`)
		.join(" ")

// Pastille d'état précisée (maquette) : rendez-vous daté, dépanneur…
export const libelleEtat = (dep, depanneur) => {
	const qui = depanneur ? ` · ${initiales(depanneur.nom)}` : ""
	switch (dep.etat) {
		case DEP_STATE_CHECK_DATA:
			return "À vérifier"
		case DEP_STATE_RENDEZ_VOUS:
			return `Rendez-vous${
				dep.date_rendez_vous
					? ` ${heureCarte(dep.date_rendez_vous)}`
					: ""
			}`
		case DEP_STATE_DISPATCHED:
			return `Dispatchée${qui}`
		case DEP_STATE_ACCEPTED:
			return `Acceptée${qui}`
		case DEP_STATE_ONGOING:
			return `En cours${qui}`
		case DEP_STATE_CANCELED:
			return "Annulée"
		default:
			return DEP_STATES[dep.etat] || "—"
	}
}

const st = {
	pied: {
		marginTop: "auto",
		paddingTop: 4,
		display: "flex",
		flexDirection: "column",
		gap: 6,
	},
	bouton: {
		width: "100%",
		padding: "7px 10px",
		borderRadius: 8,
		border: `1px solid ${COLORS.text}`,
		backgroundColor: COLORS.text,
		color: "#fff",
		fontSize: 13,
		fontWeight: 600,
		lineHeight: "18px",
		cursor: "pointer",
		fontFamily: "inherit",
		boxSizing: "border-box",
	},
	boutonSecondaire: {
		width: "100%",
		padding: "7px 10px",
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		fontSize: 13,
		fontWeight: 600,
		lineHeight: "18px",
		cursor: "pointer",
		fontFamily: "inherit",
		boxSizing: "border-box",
	},
	iconePetite: {
		width: 26,
		height: 26,
		borderRadius: 7,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.textMuted,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: 11,
		cursor: "pointer",
		padding: 0,
		boxSizing: "border-box",
	},
	heure: {
		fontSize: 12,
		color: COLORS.textLight,
		whiteSpace: "nowrap",
		alignSelf: "center",
	},
	badgeAppel: { color: COLORS.textMuted, backgroundColor: COLORS.bg },
	statut: { fontSize: 12, color: COLORS.accent, fontWeight: 600 },
	card: {
		flex: "0 0 300px",
		width: 300,
		maxWidth: "100%",
		padding: 14,
		borderRadius: 12,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		display: "flex",
		flexDirection: "column",
		gap: 6,
		boxSizing: "border-box",
	},
	header: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "space-between",
		gap: 8,
	},
	headerActions: {
		display: "flex",
		gap: 6,
		flexShrink: 0,
	},
	titre: {
		fontSize: 14,
		fontWeight: 700,
		color: COLORS.text,
		lineHeight: 1.3,
	},
	badges: {
		display: "flex",
		alignItems: "center",
		gap: 6,
		flexWrap: "wrap",
	},
	badge: {
		padding: "2px 10px",
		borderRadius: 99,
		fontSize: 11,
		fontWeight: 700,
	},
	badgeTerrain: {
		color: COLORS.accent,
		backgroundColor: COLORS.accentLight,
		display: "inline-flex",
		alignItems: "center",
		gap: 5,
	},
	badgeCouvert: {
		color: "#166534",
		backgroundColor: "#DCFCE7",
		border: "1px solid #BBF7D0",
		lineHeight: "normal",
	},
	badgeRelivraison: {
		color: COLORS.textMuted,
		backgroundColor: COLORS.bg,
		display: "inline-flex",
		alignItems: "center",
		gap: 5,
	},
	// Immatriculation en « plaque » : bordure marquée, graisse forte — l'objet
	// que le dépanneur cherche des yeux sur le terrain.
	plaque: {
		display: "inline-block",
		padding: "0 6px",
		marginRight: 6,
		border: `1.5px solid ${COLORS.text}`,
		borderRadius: 4,
		fontSize: 11,
		fontWeight: 800,
		lineHeight: "17px",
		letterSpacing: "0.06em",
		textTransform: "uppercase",
		whiteSpace: "nowrap",
	},
	// Bloc trajet : puces alignées sur la colonne d'icônes (centre à 8px),
	// connecteur pointillé vertical entre départ et destination.
	route: {
		position: "relative",
		display: "flex",
		flexDirection: "column",
		gap: 8,
		margin: "2px 0",
	},
	routeConnecteur: {
		position: "absolute",
		left: 7,
		top: 12,
		bottom: 12,
		borderLeft: `2px dotted ${COLORS.borderStrong}`,
	},
	routeRow: {
		display: "flex",
		gap: 10,
		alignItems: "flex-start",
	},
	routePuce: (plein) => ({
		width: 8,
		height: 8,
		borderRadius: "50%",
		marginTop: 5,
		marginLeft: 4,
		marginRight: 2,
		flexShrink: 0,
		boxSizing: "border-box",
		border: `2px solid ${plein ? COLORS.text : COLORS.textMuted}`,
		backgroundColor: plein ? COLORS.text : COLORS.surface,
		position: "relative",
		zIndex: 1,
	}),
	routeTexte: {
		fontSize: 13,
		color: COLORS.text,
		minWidth: 0,
		overflowWrap: "anywhere",
		textDecoration: "none",
	},
	ligne: {
		display: "flex",
		gap: 10,
		alignItems: "flex-start",
	},
	ligneIcone: {
		width: 16,
		textAlign: "center",
		fontSize: 12,
		paddingTop: 2,
		flexShrink: 0,
	},
	ligneTexte: {
		fontSize: 13,
		color: COLORS.text,
		minWidth: 0,
		overflowWrap: "anywhere",
	},
}

export default DepMissionCardV2
